import { rem, useToasts } from "@certn/cascadia";
import { sizes, textStyles } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import React, { useCallback, useEffect, useState } from "react";
import { AriaTextFieldProps, useTextField } from "react-aria";
import { useTranslation } from "react-i18next";

import { AccessibilityWarnings } from "../../../components/inputs/AccessibilityWarnings";
import { FieldErrorsProps } from "../../../components/inputs/_assets/input-wrapper/FieldErrors";
import { FormFeedback } from "../../../components/inputs/_assets/input-wrapper/FormFeedback";
import { FormLabel } from "../../../components/inputs/_assets/input-wrapper/FormLabel";
import { FormText } from "../../../components/inputs/_assets/input-wrapper/FormText";
import { InputWrapperProps } from "../../../components/inputs/_assets/input-wrapper/InputWrapper";
import { Icon } from "../../../foundations/icons/Icon/Icon";
import { useColors } from "../../../hooks/useColors";

export type ReSize = "vertical" | "none";
export interface TextAreaProps extends AriaTextFieldProps, InputWrapperProps {
  name: string;
  info?: React.ReactNode;
  clearable?: boolean;
  successMessage?: string;
  resize?: ReSize;
  maxLength?: number;
  errors?: FieldErrorsProps["children"];
  testID?: string;
}

/**
 * @deprecated see TextAreaRAC
 */
export function TextArea(props: TextAreaProps) {
  const { t } = useTranslation();
  const { show: showToast } = useToasts();
  const colors = useColors();
  const { label, testID = "text-area", resize, clearable, maxLength } = props;
  const ref = React.useRef<HTMLTextAreaElement>(null);
  const [characterCount, setCharacterCount] = useState(
    props.value?.length ?? 0
  );
  const error = props.validationState === "invalid";
  const values = textStyles["input/text-field"];

  useEffect(() => {
    setCharacterCount(props.value?.length ?? 0);
  }, [props.value]);

  const { labelProps, inputProps, descriptionProps, errorMessageProps } =
    useTextField({ ...props, inputElementType: "textarea" }, ref);

  const handleClear = useCallback(() => {
    props.onChange && props.onChange("");
  }, [props]);

  const handleCharacterCount = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!inputProps.onChange) return null;
    setCharacterCount(e.target.value.length);
    inputProps.onChange(e);
  };

  const onPaste = (e: React.ClipboardEvent) => {
    if (maxLength && e.clipboardData.getData("text").length > maxLength) {
      showToast({
        title: t(
          `The text you have pasted exceeds the max length of {{count}} characters permitted for this field.`,
          { count: maxLength }
        ),
        type: "info",
      });
    }
  };

  return (
    <>
      <FormLabel
        {...labelProps}
        isRequired={props.isRequired}
        info={props.info}
      >
        {label}
      </FormLabel>
      <div className={css(styles.helperTextStyles)}>
        <textarea
          {...inputProps}
          onPaste={onPaste}
          onChange={handleCharacterCount}
          className={css(styles.input, {
            color: "var(--text-base)",
            backgroundColor: "var(--background-input)",
            "&:invalid": {
              border: `1px solid ${colors["border-danger-input"]}`,
            },
            "::placeholder": {
              color: "var(--text-placeholder)",
            },
            resize: resize ? resize : "vertical",
            paddingRight: clearable ? "var(--space-9)" : "var(--space-3)",
            border: error
              ? `1px solid ${colors["border-danger-input"]}`
              : `1px solid ${colors["border-strong"]}`,
            fontSize: rem(sizes[values.size]),
            lineHeight: values.lineHeight,
            fontWeight: values.weight,

            "&:hover:not(:focus)": !error
              ? {
                  borderColor: "var(--border-input-active)",
                }
              : null,
            "&:focus": error
              ? css([
                  {
                    outline: "0",
                    borderColor: "var(--border-danger-input)",
                    background: "var(--background-base)",
                    boxShadow: "var(--focus-input-error)",
                  },
                ])
              : css([
                  {
                    outline: "0",
                    borderColor: "var(--border-input-active)",
                    boxShadow: "var(--focus-input)",
                  },
                ]),
          })}
          maxLength={props.maxLength}
          ref={ref}
          data-testid={testID}
        />
        {props.clearable && (
          <button
            className={css(styles.rightIconStyles, {
              color: "var(--icon-base)",
            })}
            onClick={handleClear}
          >
            <Icon name="cancel" color="icon-base" type="mini" size="space-5" />
          </button>
        )}
      </div>
      {props.description ? (
        <>
          <div className={css(styles.helperTextStyles)}>
            <FormText {...descriptionProps}>{props.description}</FormText>
            {props.maxLength && (
              <FormText className={css(styles.maxLengthStyles)}>
                {`${characterCount}/${props.maxLength}`}
              </FormText>
            )}
          </div>
          <div>
            <FormFeedback {...errorMessageProps}>{props.errors}</FormFeedback>
            <FormFeedback type="valid">{props.successMessage}</FormFeedback>
          </div>
        </>
      ) : (
        <div className={css(styles.helperTextStyles)}>
          <FormFeedback {...errorMessageProps}>{props.errors}</FormFeedback>
          <FormFeedback type="valid">{props.successMessage}</FormFeedback>
          {props.maxLength && (
            <FormText className={css(styles.maxLengthStyles)}>
              {`${characterCount}/${props.maxLength}`}
            </FormText>
          )}
        </div>
      )}
      <AccessibilityWarnings labelProps={props} />
    </>
  );
}

const styles = {
  input: css({
    width: "100%",
    height: "var(--space-20)",
    boxSizing: "border-box",
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",
    paddingLeft: "var(--space-3)",
    borderRadius: "var(--border-radius-2)",
    fontFamily: "var(--font-family-default)",
  }),
  leftIconStyles: css({
    position: "absolute",
    left: "var(--space-3)",
    top: "var(--space-2-5)",
  }),
  relativeBlock: css({
    position: "relative",
  }),
  helperTextStyles: css({
    display: "flex",
  }),
  maxLengthStyles: css({
    marginLeft: "auto",
  }),
  rightIconStyles: css({
    position: "absolute",
    right: "var(--space-3)",
    top: "var(--space-2-5)",
    background: "none",
    border: "none",
    padding: 0,

    "&:hover": {
      cursor: "pointer",
    },
  }),
};
